import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { API_BASE } from './api'
import { openRevivingEventSource } from './sse-revive'
import { isProjectFastDeliveryQueryKey, projectFastDeliveriesQueryKey } from './fast-deliveries'
import {
  isFastDeliveryRelevantEvent,
  queryKeysStaledByConversationMessage,
  queryKeysStaledByTaskEvent,
  reduceLiveEvents,
  type PlatformEvent,
} from './live-activity'

export type LiveConnectionStatus = 'connecting' | 'open' | 'disconnected'

type LiveActivityValue = {
  events: PlatformEvent[]
  runningByProject: ReturnType<typeof reduceLiveEvents>['runningByProject']
  latestByTask: ReturnType<typeof reduceLiveEvents>['latestByTask']
  connectionStatus: LiveConnectionStatus
}

const LiveActivityContext = createContext<LiveActivityValue>({
  events: [],
  runningByProject: new Map(),
  latestByTask: new Map(),
  connectionStatus: 'connecting',
})

const LIVE_LEDGER_ROOT_KEYS = new Set([
  // 会话列表（L-67，2026-09-08）：别的会话来了新消息，侧边栏要立刻知道。
  // 在这之前它不在名单里——socket 一直在，只是列表没接上它，
  // 于是新消息只能等这个 query 自己的 staleTime。arron 报的「侧边栏没有
  // 新消息标识」有一半根因在这儿：连数据都不新，打什么点都没用。
  'lulu-conversations',
  'lulu-project-conversations',
  'alerts',
  'dashboard',
  'development-tasks',
  'hosts',
  'pending-changes',
  'resources',
  'timeline',
  'today',
])

const LIVE_LEDGER_PROJECT_KEYS = new Set([
  'development-tasks',
  'fast-deliveries',
  'hosts',
  'resources',
])

const PROJECTION_RECOVERY_BACKOFF_MS = [1_000, 2_000, 4_000] as const

export function isLiveLedgerProjectionQueryKey(queryKey: readonly unknown[]): boolean {
  if (isProjectFastDeliveryQueryKey(queryKey)) return true
  const root = queryKey[0]
  if (typeof root !== 'string' || root === 'activity') return false
  if (LIVE_LEDGER_ROOT_KEYS.has(root)) return true
  if (root !== 'projects') return false
  if (queryKey.length <= 2) return true
  return typeof queryKey[2] === 'string' && LIVE_LEDGER_PROJECT_KEYS.has(queryKey[2])
}

function resetWatermark(message: MessageEvent): string | null {
  try {
    const parsed = JSON.parse(String(message.data)) as {
      cursor?: unknown
      watermark?: unknown
      payload?: { watermark?: unknown }
    }
    const value =
      parsed.payload?.watermark ?? parsed.watermark ?? parsed.cursor ?? message.lastEventId
    return typeof value === 'string' && value !== '' ? value : null
  } catch {
    return message.lastEventId || null
  }
}

/** 全站唯一 live ledger；空游标是 recent snapshot，断线续传靠 cursor。 */
export function LiveEventsProvider({ children }: { children: ReactNode }) {
  const [events, setEvents] = useState<PlatformEvent[]>([])
  const [connectionStatus, setConnectionStatus] = useState<LiveConnectionStatus>('connecting')
  const queryClient = useQueryClient()
  const lastCursor = useRef<string | null>(null)

  useEffect(() => {
    let stopped = false
    let connected = false
    let recoveryEpoch = 0
    let recoveredEpoch = 0
    let recovery: Promise<void> | null = null
    let recoveryTimer: ReturnType<typeof setTimeout> | null = null
    let recoveryAttempt = 0

    const clearRecoveryTimer = () => {
      if (recoveryTimer !== null) clearTimeout(recoveryTimer)
      recoveryTimer = null
    }

    function scheduleRecovery() {
      if (
        stopped ||
        !connected ||
        recovery !== null ||
        recoveryTimer !== null ||
        recoveryAttempt >= PROJECTION_RECOVERY_BACKOFF_MS.length
      ) {
        return
      }
      const delay = PROJECTION_RECOVERY_BACKOFF_MS[recoveryAttempt]
      recoveryAttempt += 1
      recoveryTimer = setTimeout(() => {
        recoveryTimer = null
        recoverProjections()
      }, delay)
    }

    function recoverProjections() {
      if (
        stopped ||
        !connected ||
        recovery !== null ||
        recoveryTimer !== null ||
        recoveredEpoch >= recoveryEpoch
      ) {
        return
      }
      const targetEpoch = recoveryEpoch
      let succeeded = false
      recovery = queryClient
        .invalidateQueries({ predicate: (query) => isLiveLedgerProjectionQueryKey(query.queryKey) })
        .then(() => {
          succeeded = true
          recoveredEpoch = Math.max(recoveredEpoch, targetEpoch)
        })
        .catch(() => undefined)
        .finally(() => {
          recovery = null
          if (stopped || !connected) return
          if (!succeeded) {
            scheduleRecovery()
            return
          }
          if (succeeded && recoveredEpoch < recoveryEpoch) {
            recoverProjections()
            return
          }
          if (recoveredEpoch >= recoveryEpoch) {
            recoveryAttempt = 0
            setConnectionStatus('open')
          }
        })
    }

    const resumeExhaustedRecoveryOnFact = () => {
      if (
        recoveredEpoch >= recoveryEpoch ||
        recovery !== null ||
        recoveryTimer !== null ||
        recoveryAttempt < PROJECTION_RECOVERY_BACKOFF_MS.length
      ) {
        return
      }
      recoveryAttempt = 0
      recoverProjections()
    }

    const markUnknown = () => {
      clearRecoveryTimer()
      recoveryAttempt = 0
      recoveryEpoch += 1
      setConnectionStatus('disconnected')
    }

    const stream = openRevivingEventSource({
      url: () =>
        lastCursor.current === null
          ? `${API_BASE}/events`
          : `${API_BASE}/events?cursor=${encodeURIComponent(lastCursor.current)}`,
      attach: (source, handle) => {
        source.addEventListener('stream.reset', (message) => {
          if (!handle.owns()) return
          handle.sawFact()
          lastCursor.current = resetWatermark(message as MessageEvent)
          setEvents([])
          markUnknown()
          recoverProjections()
        })
        source.onmessage = (message) => {
          if (!handle.owns()) return
          handle.sawFact()
          try {
            const parsed = JSON.parse(String(message.data)) as PlatformEvent
            if (!parsed?.cursor || !parsed.type) return
            lastCursor.current = parsed.cursor
            resumeExhaustedRecoveryOnFact()
            // 会话有新消息：侧栏与那条会话的面板立刻去取，不等 5 秒轮询。
            for (const queryKey of queryKeysStaledByConversationMessage(parsed)) {
              void queryClient.invalidateQueries({ queryKey })
            }
            for (const queryKey of queryKeysStaledByTaskEvent(parsed)) {
              void queryClient.invalidateQueries({ queryKey })
            }
            if (isFastDeliveryRelevantEvent(parsed.type) && parsed.project_id) {
              void queryClient.invalidateQueries({
                queryKey: projectFastDeliveriesQueryKey(parsed.project_id),
              })
            }
            setEvents((prev) =>
              prev.some((item) => item.cursor === parsed.cursor) ? prev : [...prev, parsed],
            )
          } catch {
            // 一条坏 JSON 不许把整条流拆掉
          }
        }
      },
      onOpen: () => {
        connected = true
        if (recoveredEpoch < recoveryEpoch) {
          recoverProjections()
          return
        }
        setConnectionStatus('open')
      },
      onError: () => {
        connected = false
        markUnknown()
      },
    })
    return () => {
      stopped = true
      clearRecoveryTimer()
      stream.close()
    }
  }, [queryClient])

  const value = useMemo(() => {
    const reduced = reduceLiveEvents(events)
    return {
      events,
      runningByProject: reduced.runningByProject,
      latestByTask: reduced.latestByTask,
      connectionStatus,
    }
  }, [connectionStatus, events])

  return <LiveActivityContext.Provider value={value}>{children}</LiveActivityContext.Provider>
}

export function useLiveActivity(): LiveActivityValue {
  return useContext(LiveActivityContext)
}
